import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { ChevronDown, ChevronUp, RefreshCw, ShieldOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Style helpers (mirrors AIAdoptionScorecard.js) ──────────────────────────
function card(isDark) {
    return {
        background: isDark ? "#0f1729" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"}`,
        boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 1px 6px rgba(0,0,0,0.04)",
    };
}

function getMaturityColors(maturity, isDark) {
    const map = {
        "AI Unprepared":         { badge: isDark ? "rgba(239,68,68,0.15)"  : "#fee2e2", text: isDark ? "#f87171" : "#b91c1c", border: isDark ? "rgba(239,68,68,0.3)"  : "#fca5a5" },
        "Early AI Adoption":     { badge: isDark ? "rgba(245,158,11,0.15)" : "#fef3c7", text: isDark ? "#fbbf24" : "#92400e", border: isDark ? "rgba(245,158,11,0.3)" : "#fde68a" },
        "AI Enabled":            { badge: isDark ? "rgba(59,130,246,0.15)" : "#dbeafe", text: isDark ? "#60a5fa" : "#1d4ed8", border: isDark ? "rgba(59,130,246,0.3)" : "#93c5fd" },
        "AI First Organization": { badge: isDark ? "rgba(16,185,129,0.15)": "#d1fae5", text: isDark ? "#34d399" : "#065f46", border: isDark ? "rgba(16,185,129,0.3)": "#6ee7b7" },
    };
    return map[maturity] || { badge: "rgba(100,116,139,0.12)", text: "#64748b", border: "rgba(100,116,139,0.2)" };
}

const PILLAR_LABELS = {
    score_data:       "Data Readiness",
    score_process:    "Process Automation",
    score_tech:       "Technology Infrastructure",
    score_workforce:  "Workforce AI Readiness",
    score_governance: "AI Governance",
    score_use_cases:  "AI Use Cases",
};

const PILLAR_KEYS = Object.keys(PILLAR_LABELS);

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

// ── Expandable row detail ────────────────────────────────────────────────────
function ResultDetail({ result, isDark }) {
    const mc = getMaturityColors(result.maturity, isDark);
    return (
        <div
            className="px-6 py-5 space-y-5"
            style={{ background: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc", borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}` }}
        >
            {/* Pillar Scores */}
            <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: isDark ? "#8b949e" : "#94a3b8" }}>
                    Pillar Breakdown
                </p>
                <div className="space-y-2.5">
                    {PILLAR_KEYS.map((key) => {
                        const val = result[key] ?? 0;
                        const pct = Math.round((val / 25) * 100);
                        return (
                            <div key={key}>
                                <div className="flex items-center justify-between mb-1 text-xs">
                                    <span style={{ color: isDark ? "#c9d1d9" : "#374151" }}>{PILLAR_LABELS[key]}</span>
                                    <span style={{ color: isDark ? "#8b949e" : "#64748b" }}>{val}/25</span>
                                </div>
                                <div className="h-1.5 rounded-full" style={{ background: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0" }}>
                                    <div
                                        className="h-1.5 rounded-full"
                                        style={{ width: `${pct}%`, background: "linear-gradient(90deg,#F87217,#FF9A4E)" }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Recommendation */}
            {result.recommendation_focus && (
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-xl p-4" style={{ background: "rgba(248,114,23,0.08)", border: "1px solid rgba(248,114,23,0.2)" }}>
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "#F87217" }}>Recommended Focus</p>
                        <p className="text-sm font-semibold mb-2" style={{ color: isDark ? "#f0f6fc" : "#0d1a3a" }}>{result.recommendation_focus}</p>
                        {result.recommendation_impact && (
                            <p className="text-xs leading-relaxed" style={{ color: isDark ? "#8b949e" : "#475569" }}>{result.recommendation_impact}</p>
                        )}
                    </div>
                    {result.recommendation_agents?.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold mb-2" style={{ color: "#F87217" }}>Suggested Agents</p>
                            <div className="space-y-1.5">
                                {result.recommendation_agents.map((agent) => (
                                    <div
                                        key={agent}
                                        className="rounded-xl px-3 py-2 text-xs font-medium flex items-center gap-2"
                                        style={{ border: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#e2e8f0"}`, background: isDark ? "rgba(255,255,255,0.03)" : "#fff", color: isDark ? "#c9d1d9" : "#374151" }}
                                    >
                                        <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: "#F87217" }} />
                                        {agent}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Metadata row */}
            <div className="flex flex-wrap gap-4 text-xs" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>
                {result.weakest_pillar && (
                    <span>Weakest Pillar: <span className="font-semibold" style={{ color: isDark ? "#c9d1d9" : "#374151" }}>{PILLAR_LABELS[`score_${result.weakest_pillar}`] || result.weakest_pillar}</span></span>
                )}
                <span>Submitted: <span className="font-semibold" style={{ color: isDark ? "#c9d1d9" : "#374151" }}>{formatDate(result.created_at)}</span></span>
            </div>
        </div>
    );
}

// ── Results table ────────────────────────────────────────────────────────────
function ResultsTable({ results, isDark }) {
    const [expandedId, setExpandedId] = useState(null);

    const toggle = (id) => setExpandedId(prev => prev === id ? null : id);

    const thStyle = { color: isDark ? "#8b949e" : "#94a3b8", fontWeight: 600, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.08em", padding: "10px 16px", whiteSpace: "nowrap" };
    const tdStyle = (isDark) => ({ padding: "12px 16px", fontSize: "0.8rem", color: isDark ? "#c9d1d9" : "#374151", verticalAlign: "middle" });

    if (results.length === 0) {
        return (
            <div className="py-20 text-center" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>
                <p className="text-sm font-medium">No scorecard submissions yet.</p>
                <p className="text-xs mt-1">Results will appear here after users complete the AI Readiness Assessment.</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse">
                <thead>
                    <tr style={{ borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}` }}>
                        <th style={thStyle} className="text-left">Company</th>
                        <th style={thStyle} className="text-left">Industry</th>
                        <th style={thStyle} className="text-left hidden md:table-cell">Employees</th>
                        <th style={thStyle} className="text-left hidden lg:table-cell">Contact</th>
                        <th style={thStyle} className="text-left hidden lg:table-cell">Email</th>
                        <th style={thStyle} className="text-center">Score</th>
                        <th style={thStyle} className="text-center hidden sm:table-cell">%</th>
                        <th style={thStyle} className="text-left">Maturity</th>
                        <th style={thStyle} className="text-left hidden xl:table-cell">Submitted</th>
                        <th style={{ ...thStyle, width: 40 }}></th>
                    </tr>
                </thead>
                <tbody>
                    {results.map((r) => {
                        const mc = getMaturityColors(r.maturity, isDark);
                        const isOpen = expandedId === r.id;
                        return (
                            <React.Fragment key={r.id}>
                                <tr
                                    onClick={() => toggle(r.id)}
                                    className="cursor-pointer transition-colors duration-150"
                                    style={{
                                        borderBottom: isOpen ? "none" : `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9"}`,
                                        background: isOpen ? (isDark ? "rgba(248,114,23,0.04)" : "rgba(248,114,23,0.02)") : "transparent",
                                    }}
                                    onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.02)" : "#fafafa"; }}
                                    onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = "transparent"; }}
                                >
                                    <td style={tdStyle(isDark)}>
                                        <span className="font-semibold">{r.company_name}</span>
                                    </td>
                                    <td style={tdStyle(isDark)}>{r.industry}</td>
                                    <td style={tdStyle(isDark)} className="hidden md:table-cell">{r.employees || "—"}</td>
                                    <td style={tdStyle(isDark)} className="hidden lg:table-cell">{r.contact_name}</td>
                                    <td style={tdStyle(isDark)} className="hidden lg:table-cell">
                                        <a href={`mailto:${r.email}`} onClick={e => e.stopPropagation()} className="hover:underline" style={{ color: "#F87217" }}>{r.email}</a>
                                    </td>
                                    <td style={{ ...tdStyle(isDark), textAlign: "center" }}>
                                        <span className="font-bold" style={{ color: "#F87217" }}>{r.total_score}</span>
                                        <span style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>/150</span>
                                    </td>
                                    <td style={{ ...tdStyle(isDark), textAlign: "center" }} className="hidden sm:table-cell">
                                        {Math.round(r.readiness_pct)}%
                                    </td>
                                    <td style={tdStyle(isDark)}>
                                        <span
                                            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold border"
                                            style={{ background: mc.badge, color: mc.text, border: `1px solid ${mc.border}` }}
                                        >
                                            {r.maturity}
                                        </span>
                                    </td>
                                    <td style={tdStyle(isDark)} className="hidden xl:table-cell whitespace-nowrap">{formatDate(r.created_at)}</td>
                                    <td style={{ ...tdStyle(isDark), textAlign: "center" }}>
                                        {isOpen
                                            ? <ChevronUp className="w-4 h-4 inline" style={{ color: "#F87217" }} />
                                            : <ChevronDown className="w-4 h-4 inline" style={{ color: isDark ? "#6e7681" : "#94a3b8" }} />
                                        }
                                    </td>
                                </tr>
                                {isOpen && (
                                    <tr style={{ borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9"}` }}>
                                        <td colSpan={10}>
                                            <ResultDetail result={r} isDark={isDark} />
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ScorecardAdmin() {
    const { isAuthenticated, loading: authLoading, user, token } = useAuth();
    const { isDark } = useTheme();

    const [results, setResults]     = useState([]);
    const [fetching, setFetching]   = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const isSuperAdmin = user?.email === "superadmin@netsmartz.com";

    const fetchResults = useCallback(async () => {
        if (!isSuperAdmin || !token) return;
        setFetching(true);
        setFetchError(null);
        try {
            const res = await fetch(`${API}/scorecard/admin/results`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "ngrok-skip-browser-warning": "true",
                },
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${res.status}`);
            }
            setResults(await res.json());
        } catch (err) {
            setFetchError(err.message || "Failed to load results");
        } finally {
            setFetching(false);
        }
    }, [isSuperAdmin, token]);

    useEffect(() => {
        if (!authLoading && isSuperAdmin) {
            fetchResults();
        }
    }, [authLoading, isSuperAdmin, fetchResults]);

    // While auth resolves show spinner
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: isDark ? "#0a0f1e" : "#f8fafc" }}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F87217]" />
            </div>
        );
    }

    // Not logged in → redirect to login, then come back here
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: "/scorecard-admin" }} replace />;
    }

    // Logged in but not superadmin
    if (!isSuperAdmin) {
        return (
            <div className="min-h-screen" style={{ background: isDark ? "#0a0f1e" : "#f8fafc" }}>
                <AppHeader />
                <div className="max-w-xl mx-auto px-4 py-24 text-center">
                    <div className="rounded-2xl p-10" style={card(isDark)}>
                        <ShieldOff className="w-12 h-12 mx-auto mb-4" style={{ color: "#ef4444" }} />
                        <h1 className="text-xl font-bold mb-2" style={{ color: isDark ? "#f0f6fc" : "#0d1a3a", fontFamily: "'Outfit',sans-serif" }}>Access Denied</h1>
                        <p className="text-sm" style={{ color: isDark ? "#8b949e" : "#475569" }}>
                            This page is restricted to the super administrator account.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ background: isDark ? "#0a0f1e" : "#f8fafc" }}>
            <AppHeader />

            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-8 space-y-6">

                {/* Header */}
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? "#f0f6fc" : "#0d1a3a" }}>
                            Scorecard Results
                        </h1>
                        <p className="text-sm mt-0.5" style={{ color: isDark ? "#8b949e" : "#475569" }}>
                            All AI Readiness Assessment submissions — {results.length} record{results.length !== 1 ? "s" : ""}
                        </p>
                    </div>
                    <button
                        onClick={fetchResults}
                        disabled={fetching}
                        className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-200"
                        style={{ background: isDark ? "rgba(248,114,23,0.12)" : "rgba(248,114,23,0.08)", color: "#F87217", border: "1px solid rgba(248,114,23,0.25)" }}
                    >
                        <RefreshCw className={`w-4 h-4 ${fetching ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>

                {/* Error state */}
                {fetchError && (
                    <div className="rounded-xl px-4 py-3 text-sm" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444" }}>
                        {fetchError}
                    </div>
                )}

                {/* Summary stats */}
                {results.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                            { label: "Total Submissions", value: results.length },
                            { label: "Avg Score", value: `${Math.round(results.reduce((s, r) => s + r.total_score, 0) / results.length)}/150` },
                            { label: "Avg Readiness", value: `${Math.round(results.reduce((s, r) => s + r.readiness_pct, 0) / results.length)}%` },
                            { label: "AI First Org", value: results.filter(r => r.maturity === "AI First Organization").length },
                        ].map(({ label, value }) => (
                            <div key={label} className="rounded-2xl p-4" style={card(isDark)}>
                                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: isDark ? "#6e7681" : "#94a3b8" }}>{label}</p>
                                <p className="text-xl font-bold" style={{ color: "#F87217" }}>{value}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Table */}
                <div className="rounded-2xl overflow-hidden" style={card(isDark)}>
                    {fetching && results.length === 0 ? (
                        <div className="py-20 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F87217]" />
                        </div>
                    ) : (
                        <ResultsTable results={results} isDark={isDark} />
                    )}
                </div>
            </div>
        </div>
    );
}
